/**
 * PrototypesRetentionWidget - ретеншен D1/D2/D3 прототипов
 * ==========================================================
 *
 * Период из сайдбара — это ДАТА УСТАНОВКИ когорты. Проекты выбираются здесь же
 * (пустой выбор = все). В таблице процент возврата и (вернулись / зрелая база):
 * D1/D2/D3 считаются только по когортам, у которых день N уже полностью прошёл.
 * Доступ к API ограничен на сервере; этот виджет только отображает данные.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { ApiService } from '../../services/api';
import { FiltersService } from '../../services/filtersService';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { FlaskConical, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { prototypes as prototypesSpecs } from '@ggstat/chart-specs';
import { filterByMinRegistrations, HiddenProjectsNote } from './prototypesMinRegistrations';

const RETENTION_KEYS = prototypesSpecs.retention.keys;
const PROJECT_COLORS = prototypesSpecs.retention.projectPalette;

const fmtInt = (value) => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (metric) => (metric && metric.pct !== null && metric.pct !== undefined ? `${metric.pct}%` : '—');

function formatDay(value) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function triggerLabel(selected) {
  if (selected.length === 0) return 'Все проекты';
  if (selected.length <= 2) return selected.join(', ');
  return `${selected.slice(0, 2).join(', ')} +${selected.length - 2}`;
}

export function ProjectFilter({ options, selected, onToggle, onClear }) {
  return (
    <div className="flex items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
            <FlaskConical className="h-3 w-3" />
            {triggerLabel(selected)}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-2">
          {options.length === 0 && (
            <p className="text-xs text-muted-foreground px-1 py-2">Проекты не найдены</p>
          )}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {options.map((project) => (
              <label
                key={project}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted/50 cursor-pointer text-xs"
              >
                <Checkbox checked={selected.includes(project)} onCheckedChange={() => onToggle(project)} />
                <span className="font-medium">{project}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClear} title="Сбросить проекты">
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export function PrototypesRetentionWidget({ minRegistrations = 0 }) {
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [metric, setMetric] = useState('d1');
  const [hiddenLines, setHiddenLines] = useState({});
  const toggleLine = (entry) => setHiddenLines((prev) => ({ ...prev, [entry.value]: !prev[entry.value] }));
  const projectsKey = selectedProjects.join(',');

  useEffect(() => {
    let cancelled = false;
    ApiService.get('/prototypes/projects')
      .then((list) => {
        if (!cancelled) setProjectOptions(Array.isArray(list) ? list : []);
      })
      .catch((error) => console.error('Не удалось загрузить проекты прототипов:', error?.message || error));
    return () => {
      cancelled = true;
    };
  }, []);

  const widgetData = useWidgetData(
    async (filters, options) => {
      const params = {
        from: filters.startDate ? FiltersService.formatDate(filters.startDate) : undefined,
        to: filters.endDate ? FiltersService.formatDate(filters.endDate) : undefined,
        project: selectedProjects
      };
      return ApiService.get('/prototypes/retention', params, { signal: options?.signal });
    },
    [projectsKey]
  );

  const widgetProps = useBaseWidget(widgetData);
  const allProjects = useMemo(
    () => (Array.isArray(widgetProps.data?.projects) ? widgetProps.data.projects : []),
    [widgetProps.data]
  );
  const projects = useMemo(
    () => filterByMinRegistrations(allProjects, minRegistrations, (project) => project.totals?.cohortSize),
    [allProjects, minRegistrations]
  );

  // На графике порог действует на каждый день установки: точка дня с малой когортой — шум.
  // Проекты без единой точки в линии не попадают (иначе пустая запись в легенде).
  const chartProjects = useMemo(
    () =>
      projects
        .map((project) => ({
          project: project.project,
          days: project.days.filter((day) => !minRegistrations || day.cohortSize >= minRegistrations)
        }))
        .filter((project) => project.days.some((day) => day[metric]?.pct !== null && day[metric]?.pct !== undefined)),
    [projects, minRegistrations, metric]
  );

  // Широкий формат для recharts: { installDate, [project]: pct }
  const chartData = useMemo(() => {
    const byDay = new Map();
    chartProjects.forEach((project) => {
      project.days.forEach((day) => {
        const row = byDay.get(day.installDate) || { installDate: day.installDate, label: formatDay(day.installDate) };
        row[project.project] = day[metric].pct;
        row[`${project.project}__base`] = day[metric].base;
        row[`${project.project}__reg`] = day.cohortSize;
        byDay.set(day.installDate, row);
      });
    });
    return [...byDay.values()].sort((a, b) => a.installDate.localeCompare(b.installDate));
  }, [chartProjects, metric]);
  const metricLabel = metric.toUpperCase();

  const toggleProject = (project) => {
    setSelectedProjects((prev) =>
      prev.includes(project) ? prev.filter((name) => name !== project) : [...prev, project].sort()
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    const visible = (payload || []).filter((entry) => !entry.hide);
    if (!active || visible.length === 0) return null;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">Ретеншен {metricLabel} · установка {label}</p>
        {visible.map((entry) => {
          const base = entry.payload[`${entry.dataKey}__base`];
          return (
            <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value === null || entry.value === undefined ? '—' : `${entry.value}%`}
              {base !== undefined && ` (база ${metricLabel} ${fmtInt(base)}, регистраций ${fmtInt(entry.payload[`${entry.dataKey}__reg`])})`}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <BaseWidget
      {...widgetProps}
      title="Прототипы: ретеншен"
      subtitle="Когорта по первому first_open, возврат — session_start в день N. Период — даты установки"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
      headerAction={
        <ProjectFilter
          options={projectOptions}
          selected={selectedProjects}
          onToggle={toggleProject}
          onClear={() => setSelectedProjects([])}
        />
      }
    >
      <div className="space-y-4">
      <HiddenProjectsNote hidden={allProjects.length - projects.length} minRegistrations={minRegistrations} />
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {allProjects.length === 0 ? 'Нет когорт установки в выбранном периоде' : 'Нет проектов с таким числом регистраций'}
        </p>
      ) : (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Проект</th>
                  <th className="py-2 pr-4 font-medium text-right">Установки</th>
                  {RETENTION_KEYS.map((key) => (
                    <th key={key} className="py-2 pr-4 font-medium text-right">{key.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.project} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{project.project}</td>
                    <td className="py-2 pr-4 text-right">{fmtInt(project.totals.cohortSize)}</td>
                    {RETENTION_KEYS.map((key) => (
                      <td key={key} className="py-2 pr-4 text-right">
                        <div className="font-medium">{fmtPct(project.totals[key])}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtInt(project.totals[key].ret)} / {fmtInt(project.totals[key].base)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">
              В скобках: вернулись / зрелая база. DN учитывает только когорты, у которых день N уже прошёл.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
            <h4 className="text-sm font-medium">Ретеншен {metricLabel} по дням установки</h4>
            <ToggleGroup
              type="single"
              value={metric}
              onValueChange={(value) => value && setMetric(value)}
              size="sm"
            >
              {RETENTION_KEYS.map((key) => (
                <ToggleGroupItem key={key} value={key} aria-label={key.toUpperCase()}>
                  {key.toUpperCase()}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            </div>
            {chartProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Нет дней установки с {metricLabel}{minRegistrations > 0 ? ` и не меньше ${minRegistrations.toLocaleString('ru-RU')} регистраций` : ''}
              </p>
            ) : (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fill: 'currentColor' }} className="text-xs" minTickGap={16} />
                <YAxis
                  domain={[0, 'auto']}
                  tick={{ fill: 'currentColor' }}
                  className="text-xs"
                  allowDecimals={false}
                  label={{ value: `% удержания ${metricLabel}`, angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend onClick={toggleLine} wrapperStyle={{ cursor: 'pointer' }} />
                {chartProjects.map((project, index) => (
                  <Line
                    key={project.project}
                    type="monotone"
                    dataKey={project.project}
                    name={project.project}
                    hide={!!hiddenLines[project.project]}
                    stroke={PROJECT_COLORS[index % PROJECT_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            )}
            {minRegistrations > 0 && (
              <p className="text-xs text-muted-foreground">
                На графике скрыты дни установки, где регистраций меньше {minRegistrations.toLocaleString('ru-RU')}.
              </p>
            )}
          </div>
        </div>
      )}
      </div>
    </BaseWidget>
  );
}

export default PrototypesRetentionWidget;
